/**
 * Support Page for trAInedup.ai
 * User-facing support ticket management
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  MessageSquare,
  Plus,
  Clock,
  CheckCircle,
  AlertCircle,
  Loader2,
  Send,
  ChevronRight,
  Star,
  ArrowLeft,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { supportService, UserTicket, CreateTicketData } from '../services/firebase/support';

/**
 * Status badge component
 */
const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const statusConfig: Record<string, { label: string; className: string }> = {
    open: { label: 'Open', className: 'bg-yellow-100 text-yellow-800' },
    in_progress: { label: 'In Progress', className: 'bg-blue-100 text-blue-800' },
    waiting_for_user: { label: 'Awaiting Your Reply', className: 'bg-purple-100 text-purple-800' },
    resolved: { label: 'Resolved', className: 'bg-green-100 text-green-800' },
    closed: { label: 'Closed', className: 'bg-gray-100 text-gray-800' },
  };

  const config = statusConfig[status] || { label: status, className: 'bg-gray-100 text-gray-800' };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.className}`}>
      {config.label}
    </span>
  );
};

/**
 * Category badge component
 */
const CategoryBadge: React.FC<{ category: string }> = ({ category }) => {
  const categoryLabels: Record<string, string> = {
    technical: 'Technical',
    billing: 'Billing',
    account: 'Account',
    course: 'Course',
    community: 'Community',
    other: 'Other',
  };

  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
      {categoryLabels[category] || category}
    </span>
  );
};

/**
 * New ticket form component
 */
const NewTicketForm: React.FC<{
  onSubmit: (data: CreateTicketData) => Promise<void>;
  onCancel: () => void;
  isSubmitting: boolean;
}> = ({ onSubmit, onCancel, isSubmitting }) => {
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<CreateTicketData['category']>('technical');
  const [priority, setPriority] = useState<'low' | 'medium' | 'high'>('medium');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!subject.trim()) {
      setError('Please enter a subject');
      return;
    }
    if (!description.trim()) {
      setError('Please describe your issue');
      return;
    }

    try {
      await onSubmit({ subject, description, category, priority });
    } catch (err: any) {
      setError(err.message || 'Failed to create ticket');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      <div>
        <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
          Subject *
        </label>
        <input
          type="text"
          id="subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          placeholder="Brief summary of your issue"
          disabled={isSubmitting}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
            Category
          </label>
          <select
            id="category"
            value={category}
            onChange={(e) => setCategory(e.target.value as CreateTicketData['category'])}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            disabled={isSubmitting}
          >
            <option value="technical">Technical Issue</option>
            <option value="billing">Billing</option>
            <option value="account">Account</option>
            <option value="course">Course Content</option>
            <option value="community">Community</option>
            <option value="other">Other</option>
          </select>
        </div>

        <div>
          <label htmlFor="priority" className="block text-sm font-medium text-gray-700 mb-1">
            Priority
          </label>
          <select
            id="priority"
            value={priority}
            onChange={(e) => setPriority(e.target.value as 'low' | 'medium' | 'high')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            disabled={isSubmitting}
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
        </div>
      </div>

      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
          Description *
        </label>
        <textarea
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={5}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          placeholder="Please describe your issue in detail. Include any relevant information that might help us assist you."
          disabled={isSubmitting}
        />
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Creating...
            </>
          ) : (
            'Create Ticket'
          )}
        </Button>
      </div>
    </form>
  );
};

/**
 * Ticket detail view component
 */
const TicketDetailView: React.FC<{
  ticket: UserTicket;
  onBack: () => void;
  onReply: (content: string) => Promise<void>;
  onRateSatisfaction: (rating: number, feedback?: string) => Promise<void>;
  isReplying: boolean;
}> = ({ ticket, onBack, onReply, onRateSatisfaction, isReplying }) => {
  const [replyContent, setReplyContent] = useState('');
  const [showRating, setShowRating] = useState(false);
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [isSubmittingRating, setIsSubmittingRating] = useState(false);

  const handleSubmitReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!replyContent.trim()) return;

    await onReply(replyContent);
    setReplyContent('');
  };

  const handleSubmitRating = async () => {
    if (rating === 0) return;
    setIsSubmittingRating(true);
    try {
      await onRateSatisfaction(rating, feedback);
      setShowRating(false);
    } finally {
      setIsSubmittingRating(false);
    }
  };

  const canReply = !['resolved', 'closed'].includes(ticket.status);
  const canRate = ['resolved', 'closed'].includes(ticket.status) && !ticket.satisfactionRating;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <button
          onClick={onBack}
          className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-gray-900">{ticket.subject}</h2>
            <StatusBadge status={ticket.status} />
          </div>
          <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
            <span>#{ticket.ticketNumber}</span>
            <CategoryBadge category={ticket.category} />
            <span>Created {ticket.createdAt?.toLocaleDateString()}</span>
          </div>
        </div>
      </div>

      {/* Satisfaction Rating (for resolved tickets) */}
      {canRate && !showRating && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-green-800 font-medium mb-2">This ticket has been resolved</p>
          <p className="text-green-700 text-sm mb-3">How would you rate your support experience?</p>
          <Button variant="outline" size="sm" onClick={() => setShowRating(true)}>
            Rate Experience
          </Button>
        </div>
      )}

      {showRating && (
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg space-y-3">
          <p className="font-medium text-gray-900">Rate your experience</p>
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                onClick={() => setRating(star)}
                className={`p-1 transition-colors ${rating >= star ? 'text-yellow-400' : 'text-gray-300'}`}
              >
                <Star className="w-8 h-8 fill-current" />
              </button>
            ))}
          </div>
          <textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="Any additional feedback? (optional)"
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSubmitRating} disabled={rating === 0 || isSubmittingRating}>
              {isSubmittingRating ? 'Submitting...' : 'Submit Rating'}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowRating(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {ticket.satisfactionRating && (
        <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span>Your rating:</span>
            <div className="flex">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  className={`w-4 h-4 ${star <= ticket.satisfactionRating! ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
                />
              ))}
            </div>
          </div>
          {ticket.satisfactionFeedback && (
            <p className="text-sm text-gray-500 mt-1">{ticket.satisfactionFeedback}</p>
          )}
        </div>
      )}

      {/* Messages/Conversation */}
      <div className="border border-gray-200 rounded-lg divide-y divide-gray-200">
        {ticket.messages.map((message) => (
          <div
            key={message.id}
            className={`p-4 ${message.senderType === 'agent' ? 'bg-blue-50' : 'bg-white'}`}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-900">
                  {message.senderType === 'agent' ? 'Support Team' : message.senderName}
                </span>
                {message.senderType === 'agent' && (
                  <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                    Support
                  </span>
                )}
              </div>
              <span className="text-xs text-gray-500">
                {message.createdAt?.toLocaleString()}
              </span>
            </div>
            <p className="text-gray-700 whitespace-pre-wrap">{message.content}</p>
          </div>
        ))}
      </div>

      {/* Reply form */}
      {canReply && (
        <form onSubmit={handleSubmitReply} className="space-y-3">
          <textarea
            value={replyContent}
            onChange={(e) => setReplyContent(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="Type your reply..."
            disabled={isReplying}
          />
          <div className="flex justify-end">
            <Button type="submit" disabled={!replyContent.trim() || isReplying}>
              {isReplying ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Reply
                </>
              )}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};

/**
 * Ticket list item component
 */
const TicketListItem: React.FC<{
  ticket: UserTicket;
  onClick: () => void;
}> = ({ ticket, onClick }) => {
  const statusIcon = {
    open: <Clock className="w-4 h-4 text-yellow-500" />,
    in_progress: <Loader2 className="w-4 h-4 text-blue-500" />,
    waiting_for_user: <AlertCircle className="w-4 h-4 text-purple-500" />,
    resolved: <CheckCircle className="w-4 h-4 text-green-500" />,
    closed: <CheckCircle className="w-4 h-4 text-gray-400" />,
  }[ticket.status] || <Clock className="w-4 h-4 text-gray-400" />;

  return (
    <button
      onClick={onClick}
      className="w-full text-left p-4 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          {statusIcon}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-medium text-gray-900 truncate">{ticket.subject}</span>
              <StatusBadge status={ticket.status} />
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <span>#{ticket.ticketNumber}</span>
              <span>·</span>
              <CategoryBadge category={ticket.category} />
              <span>·</span>
              <span>{ticket.createdAt?.toLocaleDateString()}</span>
            </div>
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
      </div>
    </button>
  );
};

/**
 * Main Support Page component
 */
export const SupportPage: React.FC = () => {
  const { user } = useAuth();
  const [tickets, setTickets] = useState<UserTicket[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<UserTicket | null>(null);
  const [showNewTicketForm, setShowNewTicketForm] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isReplying, setIsReplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'open' | 'resolved'>('all');

  const loadTickets = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const userTickets = await supportService.getUserTickets();
      setTickets(userTickets);
    } catch (err: any) {
      console.error('Error loading tickets:', err);
      setError(err.message || 'Failed to load support tickets');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadTicketDetail = useCallback(async (ticketId: string) => {
    try {
      const ticket = await supportService.getTicket(ticketId);
      if (ticket) {
        setSelectedTicket(ticket);
      }
    } catch (err: any) {
      console.error('Error loading ticket detail:', err);
      setError(err.message || 'Failed to load ticket details');
    }
  }, []);

  useEffect(() => {
    if (user) {
      loadTickets();
    }
  }, [user, loadTickets]);

  const handleCreateTicket = async (data: CreateTicketData) => {
    setIsSubmitting(true);
    try {
      await supportService.createTicket(data);
      setShowNewTicketForm(false);
      await loadTickets();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReply = async (content: string) => {
    if (!selectedTicket) return;
    setIsReplying(true);
    try {
      await supportService.addReply(selectedTicket.id, content);
      await loadTicketDetail(selectedTicket.id);
    } finally {
      setIsReplying(false);
    }
  };

  const handleRateSatisfaction = async (rating: number, feedback?: string) => {
    if (!selectedTicket) return;
    await supportService.submitSatisfaction(selectedTicket.id, rating, feedback);
    await loadTicketDetail(selectedTicket.id);
    await loadTickets();
  };

  const filteredTickets = tickets.filter((ticket) => {
    if (filter === 'all') return true;
    if (filter === 'open') return ['open', 'in_progress', 'waiting_for_user'].includes(ticket.status);
    if (filter === 'resolved') return ['resolved', 'closed'].includes(ticket.status);
    return true;
  });

  const openCount = tickets.filter((t) =>
    ['open', 'in_progress', 'waiting_for_user'].includes(t.status)
  ).length;

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Support</h1>
          <p className="text-gray-600 mt-1">
            Get help with your account, courses, or technical issues
          </p>
        </div>
        {!showNewTicketForm && !selectedTicket && (
          <Button onClick={() => setShowNewTicketForm(true)}>
            <Plus className="w-4 h-4 mr-2" />
            New Ticket
          </Button>
        )}
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* New Ticket Form */}
      {showNewTicketForm && !selectedTicket && (
        <Card className="mb-6">
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Create New Support Ticket</h2>
            <NewTicketForm
              onSubmit={handleCreateTicket}
              onCancel={() => setShowNewTicketForm(false)}
              isSubmitting={isSubmitting}
            />
          </div>
        </Card>
      )}

      {/* Ticket Detail View */}
      {selectedTicket && (
        <Card>
          <div className="p-6">
            <TicketDetailView
              ticket={selectedTicket}
              onBack={() => {
                setSelectedTicket(null);
                loadTickets();
              }}
              onReply={handleReply}
              onRateSatisfaction={handleRateSatisfaction}
              isReplying={isReplying}
            />
          </div>
        </Card>
      )}

      {/* Ticket List */}
      {!showNewTicketForm && !selectedTicket && (
        <Card>
          {/* Filter tabs */}
          <div className="border-b border-gray-200 px-4">
            <div className="flex gap-4">
              <button
                onClick={() => setFilter('all')}
                className={`py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                  filter === 'all'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                All Tickets ({tickets.length})
              </button>
              <button
                onClick={() => setFilter('open')}
                className={`py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                  filter === 'open'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Open ({openCount})
              </button>
              <button
                onClick={() => setFilter('resolved')}
                className={`py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                  filter === 'resolved'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Resolved ({tickets.length - openCount})
              </button>
            </div>
          </div>

          {/* Ticket list */}
          {filteredTickets.length === 0 ? (
            <div className="p-8 text-center">
              <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 mb-4">
                {filter === 'all'
                  ? "You haven't created any support tickets yet"
                  : filter === 'open'
                  ? 'No open tickets'
                  : 'No resolved tickets'}
              </p>
              {filter === 'all' && (
                <Button variant="outline" onClick={() => setShowNewTicketForm(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Ticket
                </Button>
              )}
            </div>
          ) : (
            <div>
              {filteredTickets.map((ticket) => (
                <TicketListItem
                  key={ticket.id}
                  ticket={ticket}
                  onClick={() => loadTicketDetail(ticket.id)}
                />
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Help section */}
      {!showNewTicketForm && !selectedTicket && (
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="font-medium text-blue-900 mb-1">Need immediate help?</h3>
          <p className="text-blue-700 text-sm">
            Our support team typically responds within 24 hours. For urgent issues, please mark your ticket as "High" priority.
          </p>
        </div>
      )}
    </div>
  );
};

export default SupportPage;
